/* eslint-disable jira/filename-case */

import defaults from '../defaults.tsx';
import withDevtools from '../enhancers/devtools.tsx';
import applyMiddleware from '../middlewares/index.tsx';
import type { StateInitializer, StoreBehavior, StoreState } from '../types.tsx';
import schedule from '../utils/schedule.tsx';

type Listener<TState> = (state: TState) => void;

function createStoreState<TState>(
	key: string,
	initialState: StateInitializer<TState>,
	behavior?: StoreBehavior | undefined,
): StoreState<TState> {
	const listeners = new Set<Listener<TState>>();
	const startState =
		typeof initialState === 'function'
			? // oxlint-disable-next-line typescript/consistent-type-assertions
				(initialState as Function)()
			: // FIXME: one should clone the initial state to avoid mutation
				initialState;

	let currentState: Partial<TState> = startState;

	const storeState: StoreState<TState> = {
		key,
		getState() {
			// oxlint-disable-next-line typescript/consistent-type-assertions -- ERROR TO BE FIXED
			return currentState as TState;
		},
		setState(nextState: Partial<TState>) {
			currentState = nextState;
			if (behavior === 'strict' || behavior === 'performance') {
				// In strict mode, we notify all handlers immediately
				// to avoid tearing and sync with other solutions (like Redux) using same model
				// And in performance("concurrent") mode we surrender all state updates to React
				storeState.notify();
			} else {
				// Instead of notifying all handlers immediately, we wait next tick
				// so multiple actions affecting the same store gets combined
				schedule(storeState.notify);
			}
		},
		resetState() {
			storeState.setState(startState);
		},
		notify() {
			for (const listener of listeners) {
				listener(storeState.getState());
			}
		},
		subscribe(listener: Listener<TState>) {
			listeners.add(listener);
			return function unsubscribe() {
				listeners.delete(listener);
			};
		},
		listeners() {
			return listeners;
		},
		// Added to satisfy StoreState<TState> type
		// oxlint-disable-next-line no-empty-function -- ERROR TO BE FIXED
		mutator: () => {},
	};
	// oxlint-disable-next-line typescript/consistent-type-assertions -- ERROR TO BE FIXED
	storeState.mutator = applyMiddleware(
		storeState,
		defaults.middlewares,
	) as StoreState<TState>['setState'];
	return storeState;
}

// oxlint-disable-next-line typescript/consistent-type-assertions -- ERROR TO BE FIXED
export default withDevtools(createStoreState) as typeof createStoreState;
